import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { FormControl, Validators } from '@angular/forms'
import { BasicFieldType } from '@app/v2/shared/enums/field.enum'
import { ValidateWebsiteUrl } from '@app/v2/shared/validators/validate-website-url'
import { ValidateLinkedInUrl } from '@app/v2/shared/validators/validate-linkedin-url'
import { TranslateModule } from '@ngx-translate/core'
import { BasicInputComponent } from '../basic-input/basic-input.component'
import { BasicInputMode } from '@app/v2/shared/enums/input-modes.enum'

@Component({
  selector: 'url-input',
  templateUrl: './url-input.component.html',
  styleUrls: [],
  standalone: true,
  imports: [BasicInputComponent, TranslateModule]
})
export class UrlInputComponent implements OnInit {
  // Form Control for input field
  @Input() control!: FormControl
  // Label for input field
  @Input() label: string = 'Website URL'
  // placeholder for input field
  @Input() placeholder: string = ''
  // Icon for input field
  @Input() icon: string = ''
  // IconType for input field
  @Input() iconType: string = 'fill'
  // Id for input field
  @Input() id: string = 'url-input'
  // Maximum characters allowed for input field
  @Input() maxCharacters: number = 200
  // Flag for input field to load
  @Input() isLoading: boolean = false
  // Flag for input field to show required
  @Input() required: boolean = false
  @Input() mode: BasicInputMode = BasicInputMode.DEFAULT
  // Flag to show clear icon for URL input
  @Input() allowClearButton: boolean = true
  // URL prefix (e.g., "linkedin.com/", "github.com/")
  @Input() urlPrefix: string = ''

  // Emit value for parent component
  @Output() onInputBlur: EventEmitter<string> = new EventEmitter<string>()
  @Output() onKeydown: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>()
  @Output() onBlurValueChange: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>()

  public basicFieldType = BasicFieldType.TEXT
  private _originalValue: string = ''

  ngOnInit(): void {
    // Set field type to URL if prefix is provided, otherwise TEXT
    this.basicFieldType = this.urlPrefix ? BasicFieldType.URL : BasicFieldType.TEXT

    // Add appropriate validation based on URL prefix
    if (this.control) {
      const validators = [Validators.maxLength(this.maxCharacters)]

      // Use LinkedIn validator for LinkedIn URLs, otherwise use general website validator
      if (this.urlPrefix === 'linkedin.com/') validators.push(ValidateLinkedInUrl)
      else validators.push(ValidateWebsiteUrl)

      this.control.addValidators(validators)
      // Store the original value for comparison
      this._originalValue = this.control.value || ''
    }
  }

  /**
   * Forward keydown event and process value on Enter key press to parent
   * @param {KeyboardEvent} event
   */
  public onKeydownEvent(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      const processedValue = this._processValue()

      // Only emit if the value actually changed
      if (processedValue !== this._originalValue) {
        this.control.markAsDirty()
        this.onInputBlur.emit(this.control.value)
        this._originalValue = processedValue
      }
    }
    this.onKeydown.emit(event)
  }

  /**
   * Handle blur event - process value and forward to parent
   * @param {FocusEvent} event
   */
  public onBlurEvent(event: FocusEvent): void {
    const processedValue = this._processValue()

    // Only emit if the value actually changed
    if (processedValue !== this._originalValue) {
      this.control.markAsDirty()
      this.onInputBlur.emit(this.control.value)
      this._originalValue = processedValue

      // Emit to parent only when value changes
      this.onBlurValueChange.emit(event)
    }
  }

  /**
   * Process the control value - handle empty and prefix-only values
   * @private
   * @returns {string} The processed value
   */
  private _processValue(): string {
    const currentValue = this.control.value

    // If value is empty, null, or undefined, set to empty string
    if (!currentValue || currentValue.trim() === '') {
      this.control.setValue('', { emitEvent: false })
      return ''
    }

    // If value only contains the prefix, set to empty string
    if (this.urlPrefix && currentValue.trim() === this.urlPrefix) {
      this.control.setValue('', { emitEvent: false })
      return ''
    }

    // If value is just a protocol prefix (http://, https://), set to empty string
    const trimmedValue = currentValue.trim()
    if (trimmedValue === 'http://' || trimmedValue === 'https://') {
      this.control.setValue('', { emitEvent: false })
      return ''
    }

    // Return the original value if no processing was needed
    return currentValue
  }
}
